import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Trash2, Settings2 } from "lucide-react";

const TYPE_LB = { stat: "Stat tile", line: "Line chart", bar: "Bar chart", pie: "Pie chart", table: "Table", gauge: "Goal dial", funnel: "Pipeline funnel", tasks: "Task list" };

function compatibleTypes(widget) {
  const qd = widget.query_def || {};
  if (widget.type === "tasks") return ["tasks"];
  if (widget.type === "funnel") return ["funnel"];
  if (widget.type === "gauge" || qd.goal_id) return ["gauge"];
  if (qd.bucket === "month") return ["line", "bar"];
  if (qd.group_by) {
    const t = ["bar", "table"];
    if ((qd.series || []).length === 1) t.splice(1, 0, "pie");
    return t;
  }
  return ["stat"];
}

export default function WidgetEditDialog({ widget, onClose, hasMgmt }) {
  const qc = useQueryClient();
  const [f, setF] = useState({
    title: widget.title, subtitle: widget.subtitle || "", type: widget.type,
    unit: widget.unit, size: widget.size, visibility: widget.visibility,
  });
  const set = (p) => setF((x) => ({ ...x, ...p }));
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["report-widgets"] });
    qc.invalidateQueries({ queryKey: ["report-widget-data", widget.id] });
  };

  const saveMut = useMutation({
    mutationFn: async () => (await api.patch(`/reports/widgets/${widget.id}`, { ...f, subtitle: f.subtitle.trim() || null })).data,
    onSuccess: () => { invalidate(); onClose(); toast.success("Widget updated"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not save widget"),
  });
  const delMut = useMutation({
    mutationFn: async () => (await api.delete(`/reports/widgets/${widget.id}`)).data,
    onSuccess: () => { invalidate(); onClose(); toast.success("Widget removed"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not delete"),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md rounded-sm" data-testid="edit-widget-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Settings2 size={16} className="text-cyan-600" />Edit Widget</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Title *</label>
            <Input value={f.title} onChange={(e) => set({ title: e.target.value })} className="rounded-sm mt-1" data-testid="widget-title-input" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Subtitle</label>
            <Input value={f.subtitle} onChange={(e) => set({ subtitle: e.target.value })} className="rounded-sm mt-1" data-testid="widget-subtitle-input" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Display as</label>
              <Select value={f.type} onValueChange={(v) => set({ type: v })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="widget-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {compatibleTypes(widget).map((t) => <SelectItem key={t} value={t}>{TYPE_LB[t]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Size</label>
              <Select value={f.size} onValueChange={(v) => set({ size: v })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="widget-size-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sm">Small tile</SelectItem>
                  <SelectItem value="half">Half width</SelectItem>
                  <SelectItem value="full">Full width</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Unit</label>
              <Select value={f.unit} onValueChange={(v) => set({ unit: v })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="widget-unit-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="count">Count</SelectItem>
                  <SelectItem value="currency">$ Currency</SelectItem>
                  <SelectItem value="percent">% Percent</SelectItem>
                  <SelectItem value="hours">Hours</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Visibility</label>
            <Select value={f.visibility} onValueChange={(v) => set({ visibility: v })}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="widget-visibility-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="public">Public — available to all staff dashboards</SelectItem>
                <SelectItem value="management" disabled={!hasMgmt}>Management private — Management access only</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-slate-400 mt-1">Public widgets will be selectable when staff build their own dashboards.</p>
          </div>
        </div>
        <DialogFooter className="flex items-center justify-between sm:justify-between">
          <Button variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-sm" data-testid="delete-widget-btn"
            onClick={() => window.confirm("Remove this widget from the dashboard?") && delMut.mutate()}>
            <Trash2 size={15} className="mr-1" />Delete
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-sm" onClick={onClose} data-testid="cancel-widget-btn">Cancel</Button>
            <Button className="bg-cyan-600 hover:bg-cyan-700 rounded-sm" data-testid="save-widget-btn"
              disabled={!f.title.trim() || saveMut.isPending} onClick={() => saveMut.mutate()}>
              Save changes
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
